import type { NotificationCategory, NotificationItem } from "@/types/notifications"
import { getNotificationEventLabel } from "@/constants/notificationEventLabels"
import {
  getNotificationDisplayHtml,
  resolveNotificationDisplayTitle,
} from "@/utils/notificationDisplay"

/**
 * Local inbox samples for styling every catalog notification type.
 * Inject via Notifications Centre → “Load style samples”, or
 * `/notifications?seedStyleNotifications=1`.
 *
 * IDs are prefixed `style-seed-` so read/delete API calls are skipped.
 * Titles use canonical labels from `notificationEventLabels`.
 */
export const STYLE_SEED_ID_PREFIX = "style-seed-"

export function isStyleSeedNotificationId(id: string): boolean {
  return id.startsWith(STYLE_SEED_ID_PREFIX)
}

type SeedDef = {
  eventKey: string
  category: NotificationCategory | "reports"
  /** Optional override; defaults to canonical event label. */
  title?: string
  bodyMarkdown: string
  data?: Record<string, unknown>
}

const ACCOUNT = "NC-PRO-001"
const NOW = Date.now()

function minutesAgo(mins: number): string {
  return new Date(NOW - mins * 60_000).toISOString()
}

const SEED_DEFS: SeedDef[] = [
  // —— Performance summaries (styled) ——
  {
    eventKey: "performance.summary.daily",
    category: "performance",
    bodyMarkdown: "Your performance summary for May 2, 2026 is ready.",
    data: {
      period_label: "2026-05-02",
      period_type: "daily",
      period_start_local: "2026-05-02T00:00:00+00:00",
      period_end_local: "2026-05-03T00:00:00+00:00",
      timezone: "UTC",
      account_code: ACCOUNT,
      broker_account_code: ACCOUNT,
      total_pnl: 123.45,
      total_return_pct: 2.34,
      total_trades: 5,
      closed_trades: 5,
      open_trades: 2,
      winning_trades: 3,
      losing_trades: 2,
      scratch_trades: 0,
      win_rate: 60,
      long_trades: 3,
      short_trades: 2,
      average_trade_pnl: 24.69,
      average_win: 50,
      average_loss: -35,
      largest_win: 80,
      largest_loss: -40,
      max_drawdown: -15,
      risk_reward_ratio: 1.43,
      symbol_breakdown: [
        { symbol: "AAPL", count: 3, total_pnl: 100 },
        { symbol: "TSLA", count: 2, total_pnl: 23.45 },
      ],
    },
  },
  {
    eventKey: "performance.summary.weekly",
    category: "performance",
    bodyMarkdown: "Your weekly performance summary is ready.",
    data: {
      period_label: "2026-04-28 — 2026-05-04",
      period_type: "weekly",
      period_start_local: "2026-04-28T00:00:00+00:00",
      period_end_local: "2026-05-05T00:00:00+00:00",
      timezone: "UTC",
      account_code: ACCOUNT,
      broker_account_code: ACCOUNT,
      total_pnl: 456.78,
      total_return_pct: 3.21,
      total_trades: 12,
      closed_trades: 12,
      open_trades: 1,
      winning_trades: 8,
      losing_trades: 4,
      win_rate: 66.67,
      long_trades: 7,
      short_trades: 5,
      average_trade_pnl: 38.07,
      average_win: 62.5,
      average_loss: -45,
      largest_win: 110,
      largest_loss: -70,
      max_drawdown: -120,
      risk_reward_ratio: 1.39,
      symbol_breakdown: [
        { symbol: "EURUSD", count: 5, total_pnl: 200 },
        { symbol: "AAPL", count: 4, total_pnl: 156.78 },
      ],
    },
  },
  {
    eventKey: "performance.summary.monthly",
    category: "performance",
    bodyMarkdown: "Your monthly performance summary is ready.",
    data: {
      period_label: "2026-04-01 — 2026-04-30",
      period_type: "monthly",
      period_start_local: "2026-04-01T00:00:00+00:00",
      period_end_local: "2026-05-01T00:00:00+00:00",
      timezone: "UTC",
      account_code: ACCOUNT,
      broker_account_code: ACCOUNT,
      total_pnl: 987.65,
      total_return_pct: 6.54,
      total_trades: 34,
      closed_trades: 34,
      open_trades: 0,
      winning_trades: 20,
      losing_trades: 14,
      win_rate: 58.82,
      long_trades: 18,
      short_trades: 16,
      average_trade_pnl: 29.05,
      average_win: 75,
      average_loss: -55,
      largest_win: 220,
      largest_loss: -150,
      max_drawdown: -400,
      risk_reward_ratio: 1.36,
      symbol_breakdown: [
        { symbol: "XAUUSD", count: 12, total_pnl: 500 },
        { symbol: "EURUSD", count: 12, total_pnl: 687.65 },
      ],
    },
  },
  // —— Other performance ——
  {
    eventKey: "performance.daily.new_best_day",
    category: "performance",
    bodyMarkdown:
      "Nice work. **May 2, 2026** is your new best trading day. You closed the session at **+$123.45** (**+2.34%**), beating your previous best of **+$110.00** from **April 25, 2026**.",
    data: {
      period_label: "2026-05-02",
      account_code: ACCOUNT,
      pnl_abs: 123.45,
      pnl_pct: 2.34,
      trades_closed: 5,
      milestone_type: "best_day",
      previous_record_pnl_abs: 110,
      previous_record_date: "2026-04-25",
    },
  },
  {
    eventKey: "performance.daily.new_worst_day",
    category: "performance",
    bodyMarkdown:
      "**May 2, 2026** is your new worst trading day. Big losing days are normal. They're part of the game. You closed the session at **-$123.45** (**-2.34%**), below your previous worst of **-$110.00** from **April 18, 2026**.",
    data: {
      period_label: "2026-05-02",
      account_code: ACCOUNT,
      pnl_abs: -123.45,
      pnl_pct: -2.34,
      trades_closed: 5,
      milestone_type: "worst_day",
      previous_record_pnl_abs: -110,
      previous_record_date: "2026-04-18",
    },
  },
  {
    eventKey: "performance.pnl.milestone_crossed",
    category: "performance",
    bodyMarkdown:
      "You crossed **+$1,000** generated P&L on account **NC-PRO-001** (now +$1,250 / +12.5%).",
    data: {
      account_code: ACCOUNT,
      milestone_type: "absolute",
      milestone_value: 1000,
      direction: "up",
      baseline_generated_pnl: 10000,
      current_generated_pnl: 11250,
      delta_generated_pnl: 1250,
      current_return_pct: 12.5,
      crossed_at: "2026-05-03T14:22:00+00:00",
      display_currency: "USD",
      timezone: "America/New_York",
    },
  },
  // —— System ——
  {
    eventKey: "system.broker_disconnected",
    category: "system",
    bodyMarkdown:
      "Your **Liquid Brokers** connection was lost. Syncing is paused until it reconnects.",
    data: { account_code: ACCOUNT, broker_name: "liquidbrokers" },
  },
  {
    eventKey: "system.broker_reconnected",
    category: "system",
    bodyMarkdown:
      "Your **Liquid Brokers** connection is back online. Syncing has resumed.",
    data: { account_code: ACCOUNT, broker_name: "liquidbrokers" },
  },
  {
    eventKey: "system.sync_failed",
    category: "system",
    bodyMarkdown:
      "We couldn't sync trades for **NC-PRO-001**. Last error: connection timed out. We'll retry automatically.",
    data: {
      account_code: ACCOUNT,
      broker_name: "liquidbrokers",
      error: "connection timed out",
    },
  },
  {
    eventKey: "system.sync_recovered",
    category: "system",
    bodyMarkdown:
      "Trade sync for **NC-PRO-001** is healthy again. Your latest fills should appear shortly.",
    data: { account_code: ACCOUNT, broker_name: "liquidbrokers" },
  },
  // —— Billing ——
  {
    eventKey: "billing.payment_succeeded",
    category: "billing",
    bodyMarkdown: "Thanks. We received **99.00 USD**. Your access continues as normal.",
    data: { amount_paid_display: "99.00", currency: "USD" },
  },
  {
    eventKey: "billing.payment_failed",
    category: "billing",
    bodyMarkdown: "We couldn't process your subscription payment (attempt **2**). Please update your card.",
    data: {
      attempt_count: 2,
      action_url: "/profile?tab=billing",
      action_label: "Manage payment",
    },
  },
  {
    eventKey: "billing.invoice.payment_action_required",
    category: "billing",
    bodyMarkdown:
      "Your bank needs a quick authentication step (SCA / 3DS) to complete payment. This isn't a failed charge.",
    data: {
      hosted_invoice_url: "https://invoice.stripe.com/i/acct_example/test",
      action_url: "https://invoice.stripe.com/i/acct_example/test",
      action_label: "Confirm payment",
    },
  },
  {
    eventKey: "billing.subscription.cancelled",
    category: "billing",
    bodyMarkdown: "Your Tessera subscription has been cancelled. You can re-activate anytime.",
    data: {
      action_url: "/profile?tab=billing",
      action_label: "Re-activate",
    },
  },
  {
    eventKey: "billing.subscription.updated",
    category: "billing",
    bodyMarkdown: "Your subscription status was updated to **active**. Review billing details if anything looks off.",
    data: { status: "active", action_url: "/profile?tab=billing" },
  },
  // —— Reports ——
  {
    eventKey: "report_ready",
    category: "reports",
    bodyMarkdown: "Your Performance Review report (May session review) is ready to view.",
    data: {
      report_id: "style-seed-report-pr",
      report_type: "trade-analysis",
      title: "May session review",
      report_name: "May session review",
      action_url: "/reports/style-seed-report-pr",
    },
  },
  {
    eventKey: "report_ready",
    category: "reports",
    bodyMarkdown: "Your Entry Analysis report (this weeks results) is ready to view.",
    data: {
      report_id: "style-seed-report-ra",
      report_type: "market-context-performance",
      title: "this weeks results",
      report_name: "this weeks results",
      action_url: "/reports/style-seed-report-ra",
    },
  },
  {
    eventKey: "report_ready",
    category: "reports",
    bodyMarkdown: "Your Exit Placement report (BB Entry Exit Placement) is ready to view.",
    data: {
      report_id: "style-seed-report-ep",
      report_type: "trade-management",
      title: "BB Entry Exit Placement",
      report_name: "BB Entry Exit Placement",
      action_url: "/reports/style-seed-report-ep",
    },
  },
  {
    eventKey: "report_failed",
    category: "reports",
    bodyMarkdown:
      "We couldn't generate your Performance Review report (May session review).",
    data: {
      report_id: "style-seed-report-fail",
      report_type: "trade-analysis",
      title: "May session review",
      report_name: "May session review",
      message: "Generation timed out. Please try again.",
      action: "Try again from Reports when you're ready.",
    },
  },
  // —— Announcement ——
  {
    eventKey: "announcement.platform",
    category: "announcement",
    bodyMarkdown:
      "We shipped improvements to the dashboard and notifications centre. **Thanks for trading with us.**",
    data: { action_url: "/dashboard", action_label: "Open dashboard" },
  },
]

/** All catalog-style sample notifications for inbox styling. */
export function buildNotificationStyleSeedItems(): NotificationItem[] {
  return SEED_DEFS.map((def, index) => {
    const baseTitle = def.title ?? getNotificationEventLabel(def.eventKey) ?? def.eventKey
    const item: NotificationItem = {
      id: `${STYLE_SEED_ID_PREFIX}${index}-${def.eventKey.replace(/\./g, "-")}`,
      category: def.category as NotificationCategory,
      eventKey: def.eventKey,
      title: resolveNotificationDisplayTitle(baseTitle, def.eventKey, def.data ?? null),
      bodyMarkdown: def.bodyMarkdown,
      statusInApp: "unread" as const,
      createdAt: minutesAgo(index),
      data: def.data ?? null,
    }
    item.bodyMarkdown = getNotificationDisplayHtml(item)
    return item
  })
}

export function notificationStyleSeedCount(): number {
  return SEED_DEFS.length
}
